<template>
  <div>
    <div
      :class="['customized-file-upload rounded position-relative d-flex flex-column', containerClass, { 'border-danger': !!fileError.file && !uploadedFile }]">
      <div class="d-flex align-items-center justify-content-center file-upload-trigger">
        <img src="@/assets/images/upload-file.svg" alt="upload" class="mr-2"> Drag &amp; drop your file here or
        <span class="text-primary ml-1">browse</span>
      </div>
      <b-form-file id="file-uploader-input" v-model="file" :accept="filetypes" ref="file-input"
        class="position-absolute w-100 h-100 cursor-pointer"></b-form-file>
      <div class="uploaded-file d-flex mt-2" v-if="uploadedFile || imgLoading">
        <div :class="['flex-grow-1 img-container rounded position-relative', { 'border-danger': !!fileError.file }]">
          <template v-if="(!!(fileError.fileType) || !!(fileError.fileSize)) && !imgLoading">
            <div
              :class="['position-absolute file-error text-center d-flex align-items-center justify-content-center w-100 h-100 p-2 rounded', { 'border border-danger': !!(fileError.fileType) || !!(fileError.fileSize) }]">
              {{ fileError.fileType || fileError.fileSize }}
            </div>
          </template>
          <div class="img-overflow overflow-hidden rounded placeholder">
            <img :src="uploadedFile" alt="id" ref="image-holder" v-if="!imgLoading">
            <b-spinner small label="Small Spinner" v-else></b-spinner>
          </div>
          <b-button class="position-absolute remove-img bg-white p-0 rounded-circle border-0 shadow-none d-flex align-items-center justify-content-center"
            @click="resetFile" v-if="!imgLoading">
            <img src="@/assets/images/remove-neutral.svg" alt="remove">
          </b-button>
        </div>
        <div class="flex-grow-1 img-container rounded position-relative"></div>
        <div class="flex-grow-1 img-container rounded position-relative"></div>
        <div class="flex-grow-1 img-container rounded position-relative"></div>
      </div>
    </div>
    <b-form-invalid-feedback :force-show="!!(fileError.file) && !imgLoading">
      {{ fileError.file }}
    </b-form-invalid-feedback>
    <div class="d-flex align-items-center justify-content-between mt-2">
      <small>{{ acceptedFileTypesLabel }}</small>
      <small>Maximum file size: 2mb</small>
    </div>
  </div>
</template>

<script>
import { required, helpers } from "vuelidate/lib/validators";
import base64 from "convertto-base64";
import heic2any from "heic2any";
import imageCompression from 'browser-image-compression';

export default {
  name: "FileUpload",
  props: ["templateValue", "filetypes", "acceptedFileTypesLabel", "containerClass", "formError"],
  data() {
    return {
      file: null,
      uploadedFile: null,
      fileTypesValidation: [],
      fieldValidations: {
        file: ["filetypes", "fileSize"]
      },
      validationMessages: {
        required: "Upload the required file type and try again.",
        fileTypes: "This file type isn't supported.",
        fileSize: "File size should be less than 2 MB."
      },
      MaciOS: ["Mac OS", "iOS"],
      MaciOSFileTypes: ["heic"],
      imgLoading: false
    }
  },
  computed: {
    fileError() {
      const $v = this.$v;
      const errors = {};
      if (this.formError || $v.$invalid && $v.$dirty && !this.imgLoading) errors["file"] = this.validationMessages.required;
      if (
        !this.imgLoading &&
        $v.$invalid &&
        $v.file.required &&
        !$v.file.filetypes
      ) errors["fileType"] = this.validationMessages.fileTypes;
      else if (
        !this.imgLoading &&
        $v.$invalid &&
        $v.file.required &&
        $v.file.filetypes &&
        !$v.file.fileSize
      ) errors["fileSize"] = this.validationMessages.fileSize;
      return errors;
    },
    imgHolder() {
      return this.$refs["image-holder"] || null;
    }
  },
  validations: {
    file: {
      required,
      filetypes: function filetypes(value) {
        let acceptableTypes = this.filetypes.split(",").map(type => type.split(".").pop().toLowerCase());
        let type = null;
        if (this.MaciOS.includes(this.getOperatingSystem()))
          acceptableTypes = acceptableTypes.concat(this.MaciOSFileTypes);
        if (value) {
          const { name } = value;
          type = name.split(".").pop();
        }
        return !helpers.req(value) || acceptableTypes.includes(type.toLowerCase());
      },
      fileSize: async function fileSize(value) {
        let currentFileSize = null;
        if (value) {
          const type = value.name.split(".").pop().toLowerCase();
          this.imgLoading = true;
          if (this.MaciOSFileTypes.includes(type)) {
            await this.convertHeicImage(value).then(async (convertedImage) => {
              await this.compressImage(convertedImage).then(async (compressedImage) => {
                this.uploadedFile = await base64(compressedImage);
                this.$emit("fileUpdated", this.uploadedFile);
                currentFileSize = this.getSizeToMb(compressedImage.size);
                this.imgLoading = false;
              });
            });
          } else {
            await this.compressImage(value).then(async (compressedImage) => {
              this.uploadedFile = await base64(compressedImage);
              this.$emit("fileUpdated", this.uploadedFile);
              currentFileSize = this.getSizeToMb(compressedImage.size);
              this.imgLoading = false;
            });
          }
        }
        return !helpers.req(value) || this.isValidSize(currentFileSize);
      }
    }
  },
  methods: {
    resetFile() {
      this.$refs["file-input"].reset();
      this.uploadedFile = null;
      this.$emit("fileUpdated", null);
    },
    isValidSize(currentFileSize) {
      const maxsize = 2.00;
      return parseFloat(currentFileSize) <= Number(maxsize.toFixed(2));;
    },
    isMaciOsFileType() {
      const type = this.file.name.split(".").pop().toLowerCase();
      return this.MaciOSFileTypes.includes(type);
    },
    getOperatingSystem() {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera
      const platform = navigator.platform

      if (/windows phone/i.test(userAgent)) {
        return "Windows Phone";
      }

      if (/android/i.test(userAgent)) {
        return "Android";
      }

      if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        return "iOS";
      }

      if (/Mac/i.test(platform)) {
        return "Mac OS";
      }

      if (/Win/i.test(platform)) {
        return "Windows";
      }

      if (/Linux/i.test(platform)) {
        return "Linux";
      }

      if (/Macintosh|Mac OS X/.test(userAgent) && !/like Mac OS X/.test(userAgent)) {
        return "Mac OS";
      }

      if (/Win(dows )?NT|Windows NT/i.test(userAgent)) {
        return "Windows";
      }

      if (/Linux/i.test(userAgent)) {
        return "Linux";
      }

      return "unknown";
    },
    getSizeToMb(size) {
      return (size * 0.00000095367432).toFixed(4);
    },
    convertHeicImage(value) {
      return new Promise((resolve, reject) => {
        heic2any({
          blob: value,
          toType: "image/jpeg"
        }).then(async (resultInBlob) => {
          return resolve(resultInBlob);
        }).catch((err) => {
          reject(err);
        })
      })
    },
    async compressImage(file) {
      const options = {
        maxSizeMB: 2
      }
      try {
        if (parseFloat(this.getSizeToMb(file.size)) > options.maxSizeMB)
          return await imageCompression(file, options);
        else return file;
      } catch (error) {
        console.log(error);
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.customized-file-upload {
  border: 1.5px dashed #D8DCDE;
  color: #2F3941;

  .file-upload-trigger {
    height: 68px;
  }

  &>.custom-file {
    opacity: 0;
    top: 0;
    left: 0;
  }

  &~div>small {
    font-size: 12px;
    color: #87929D;
  }
}

.uploaded-file {
  padding: 0 16px 24px;
  font-size: 13px;
  color: #2F3941;

  .img-container {
    background: #F8F9F9;
    aspect-ratio: 1/1;
    min-height: 45px;
    max-width: calc(100% / 4);
    width: 100%;

    &:not(:last-child) {
      margin-right: 12px;
    }

    &>.img-overflow {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      &>img {
        max-width: 150px;
        max-height: 150px;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .remove-img {
      top: -10px;
      right: -5px;
      width: 19px;
      height: 19px;
    }

    .file-error {
      background: rgba($color: #fff, $alpha: 0.85);
      color: #DC3545;
      font-size: 13px;
    }
  }
}
</style>

<style lang="scss">
#file-uploader-input {
  height: 100%;
}
</style>
